import { pipe } from 'ramda';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Button } from 'components/atoms/button';
import { Checkbox } from 'components/atoms/checkbox';
import { Input, TextArea } from 'components/atoms/input';
import {
  durationParser,
  formatter,
  getError,
  getMinutes,
  nonEmpty,
  useInputDuration,
  withGreaterThanZero,
  withZeroAsError,
} from 'components/atoms/input-duration';
import { Select } from 'components/atoms/select';
import { Color } from 'components/colors';
import { withLessThanOneDayParser } from 'components/organisms/timesheets/common/utils';
import {
  asList,
  useCategories,
} from 'components/pages/timesheet-settings/categories';
import {
  filter,
  isEmpty,
  map,
  Maybe,
  ofNullable,
  orElse,
  orUndefined,
} from 'core/maybe';
import { maybeValueOf } from 'core/state';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { BodyShort01 } from 'ozone/atoms';
import { useTimesheetConfiguration } from 'queries/timesheets/configuration';
import { getRandomColor } from 'utils/get-random-color';
import {
  ButtonGroup,
  Categories,
  Duration,
  Form,
  Notes,
  Row,
} from './components';
import { DrawerContent } from './drawer-content';
import { useTimeSpent } from './use-time-spent';
import { viewTimesheetButton } from './view-timesheet-button';

type Option = {
  label: string;
  value: number;
  color: Color;
};

interface Props {
  minutes: Maybe<number>;
  onMinutesChange: (next: Maybe<number>) => void;
  billable: boolean;
  onBillableChange: (next: boolean) => void;
  category: Maybe<number>;
  onCategoryChange: (next: Maybe<number>) => void;
  notes: Maybe<string>;
  onNotesChange: (next: Maybe<string>) => void;
  onSave: () => void;
  onCancel: () => void;
  timeSpent: ReturnType<typeof useTimeSpent>['timeSpent'];
}

export const AddTimeEntryDrawerControlled: FC<Props> = ({
  minutes,
  onMinutesChange,
  billable,
  onBillableChange,
  category: categoryId,
  onCategoryChange: setCategoryId,
  notes,
  onNotesChange,
  onSave,
  onCancel,
  timeSpent,
}) => {
  const parser = pipe(
    () => durationParser,
    withGreaterThanZero,
    withZeroAsError,
    withLessThanOneDayParser,
    nonEmpty,
  )();
  const { data: configuration } = useTimesheetConfiguration();
  const { configMeta } = configuration || {};
  const { categoryMandatory } = configMeta || {};

  const { categories } = useCategories();
  const { handleBlur, handleChange, handlePressEnter, value, state } =
    useInputDuration(minutes, parser);
  const duration = useMemo(() => getMinutes(state), [state]);
  const error = useMemo(() => getError(state), [state]);

  useEffect(() => onMinutesChange(duration), [duration, onMinutesChange]);
  const translate = useTranslate();
  const categoryOptions: Option[] = useMemo(
    () =>
      asList(categories).map(([, { id, name }]) => ({
        value: id,
        label: name,
        color: getRandomColor(id),
      })),
    [categories],
  );
  const category = useMemo(
    () =>
      ofNullable(
        categoryOptions.find((x) => x.value === categoryId[1]) ?? null,
      ),
    [categoryId, categoryOptions],
  );
  const selectCategory = useCallback(
    (option?: Option) =>
      pipe(
        () => option ?? null,
        ofNullable,
        map((x) => x.value),
        setCategoryId,
      )(),
    [setCategoryId],
  );
  const saveCaption = useTranslation('task.add_time_entry.save');
  const primary = useMemo(
    () => ({
      title: saveCaption,
      onClick: onSave,
      disabled: categoryMandatory
        ? isEmpty(minutes) || isEmpty(categoryId)
        : isEmpty(minutes),
    }),
    [saveCaption, onSave, categoryMandatory, minutes, categoryId],
  );
  const cancelCaption = useTranslation('task.add_time_entry.cancel');
  const secondary = useMemo(
    () => ({
      title: cancelCaption,
      onClick: onCancel,
    }),
    [cancelCaption, onCancel],
  );
  const formattedTimeSpent = useMemo(
    () => pipe(() => timeSpent, maybeValueOf, map(formatter), orElse('...'))(),
    [timeSpent],
  );
  const validateStatus = useMemo(
    () =>
      pipe(
        () => error,
        filter((e) => e !== 'empty'),
        map(() => 'error'),
        orUndefined,
      )(),
    [error],
  );

  return (
    <DrawerContent>
      <DrawerContent.Title data-cy="task.add_time_entry.title">
        {useTranslation('task.add_time_entry')}
      </DrawerContent.Title>
      <DrawerContent.Content>
        <Row>
          <BodyShort01 data-cy="task.add_time_entry.total_time_spent">
            {useTranslation('task.add_time_entry.total_time_spent')}:{' '}
            <strong>{formattedTimeSpent}</strong>
          </BodyShort01>
        </Row>
        <Form layout="inline">
          <Duration
            required
            validateStatus={validateStatus}
            label={useTranslation('task.add_time_entry.duration')}
          >
            <Input
              placeholder={useTranslation(
                'timesheets.placeholders.time_tracked',
              )}
              id="task_pane_time_entry_duration"
              value={value}
              onChange={handleChange}
              onPressEnter={handlePressEnter}
              onBlur={handleBlur}
            />
          </Duration>
          <Form.Item label={useTranslation('task.add_time_entry.billable')}>
            <Checkbox
              data-cy="task.add_time_entry.billable"
              checked={billable}
              onChange={(e) => onBillableChange(e.target.checked)}
            />
          </Form.Item>
        </Form>
        <Form layout="inline">
          <Categories
            label={useTranslation('task.add_time_entry.category')}
            required={categoryMandatory}
          >
            <Select
              type="tag"
              options={categoryOptions}
              value={orUndefined(category)}
              onChange={selectCategory}
              isClearable
            />
          </Categories>
        </Form>
        <Form layout="inline">
          <Notes label={useTranslation('task.add_time_entry.notes')}>
            <TextArea
              value={orUndefined(notes)}
              placeholder={useTranslation('timesheets.placeholders.notes')}
              onChange={(e) =>
                onNotesChange(ofNullable(e.target.value ?? null))
              }
            />
          </Notes>
        </Form>
        <Row data-cy="task.add_time_entry.view_timesheet">
          {viewTimesheetButton(translate)}
        </Row>
      </DrawerContent.Content>
      <DrawerContent.Footer>
        <ButtonGroup $reversed>
          <Button
            data-cy="task.add_time_entry.save"
            type="primary"
            onClick={primary.onClick}
            disabled={primary.disabled}
          >
            {primary.title}
          </Button>
          <Button
            data-cy="task.add_time_entry.cancel"
            onClick={secondary.onClick}
          >
            {secondary.title}
          </Button>
        </ButtonGroup>
      </DrawerContent.Footer>
    </DrawerContent>
  );
};
